import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Container, Navbar, Collapse, Nav, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { logout, validateToken } from '../services/seguridad/auth-service';
import { getMenu } from '../services/configuracion/usuario-service';
import UserDropdown from '../components/User';
import Sidebar from '../components/Sidebar';
import { removeToken, getToken, removeUserData } from '../utils/util';

const Main = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSearching, setIsSearching] = useState(false);
  const [Module, setModule] = useState(null);
  const [open, setOpen] = useState({});
  const [menuData, setMenuData] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const menuLoadedRef = React.useRef(false);

  useEffect(() => {
    const checkToken = async () => {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const valid = await validateToken();
        if (!valid) {
          // Token inválido o vencido: limpiar y redirigir
          removeToken();
          removeUserData();
          navigate('/login');
        }
      } catch (error) {
        // En caso de error al validar, eliminar credenciales y forzar login
        removeToken();
        removeUserData();
        navigate('/login');
      }
    };
    checkToken();
  }, [navigate]);
  
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      if (!isSearching) {
        setIsSidebarOpen(newWidth >= 768);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSearching]);

  const handleLogout = async () => {
    try {
      removeToken();
      removeUserData();
      navigate('/login');
      await logout();
    } catch (error) {
      console.error('Error durante el cierre de sesión:', error);
    }
  };

  useEffect(() => {
    const loadMenu = async () => {
      const token = getToken();
      if (!token || menuLoadedRef.current) return;

      try {
        const data = await getMenu();
        //console.log('Loading menu data:', data);
        setMenuData(data);
        menuLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading menu:', error);
      }
    };
    loadMenu();
  }, []);

  const sidebarWidth = windowWidth >= 768 ? '250px' : '100%';

  const handleMenuClick = (module) => {
    setModule(module);
    setLoadError(false);
    if (windowWidth < 768) setIsSidebarOpen(false);
  };

  const handleToggle = (cod, submenu) => {
    setOpen((prevState) => ({
      ...prevState,
      [cod]: !prevState[cod]
    }));

    if (!submenu) {
      setModule(cod);
      setLoadError(false);
    }
  };

  const renderModule = () => {
    if (!Module) return null;

    const ModuleComponent = lazy(() =>
      import(`../modules/${Module}`).catch((error) => {
        console.error('Error loading module:', error);
        setLoadError(true);
      })
    );

    return (
      <Suspense fallback={<div>Cargando...</div>}>
        {loadError ? (
          <div>Error: Módulo o componente no encontrado.</div>
        ) : (
          <ModuleComponent />
        )}
      </Suspense>
    );
  };

  return (
    <div id="wrapper">
      <Navbar expand="lg" className="p-1 border-bottom sticky-top" bg="dark" variant="dark">
        <Container fluid className="p-0">
          <Navbar.Toggle onClick={() => setIsSidebarOpen(!isSidebarOpen)} aria-controls="sidebar-collapse" />
          <Navbar.Brand className='ps-2'> <i className="fa fa-desktop"></i> Tienda</Navbar.Brand>
          <UserDropdown onLogout={handleLogout} />
        </Container>
      </Navbar>
      <div className="d-flex align-content-start flex-column flex-md-row">
        <Collapse in={isSidebarOpen} className="bg-transparent" id="sidebar-collapse" style={{ width: sidebarWidth, minWidth: '200px' }}>
          <Nav className="flex-column list-group">
            <ListGroup className="rounded-0">
              <Sidebar menu={menuData} onMenuClick={handleMenuClick} open={open} handleToggle={handleToggle} onSearchFocus={setIsSearching} />
            </ListGroup>
          </Nav>
        </Collapse>
        <div id="pagina" className="p-1 w-100">
          {renderModule()}
        </div>
      </div>
    </div>
  );
};

export default Main;
